import React, { createContext, useState, useEffect, useContext } from 'react';
import { getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';

const FirebaseContext = createContext(null);

export function FirebaseProvider({ children }) {
  const [firebaseInitialized, setFirebaseInitialized] = useState(false);
  const [initializationError, setInitializationError] = useState(null);
  const [initializationAttempted, setInitializationAttempted] = useState(false);

  useEffect(() => {
    // Check if Firebase is initialized
    const checkFirebase = async () => {
      if (initializationAttempted) return; // Prevent multiple init attempts during StrictMode
      
      setInitializationAttempted(true);
      try {
        // Try to get the Firebase app instance
        const app = getApp();
        // Try to initialize Firestore to verify everything is working
        getFirestore(app);
        
        // If we get here, Firebase is initialized
        setFirebaseInitialized(true);
      } catch (error) {
        console.error("Firebase initialization error:", error);
        setInitializationError(error.message);
      }
    };

    // Small delay to ensure app is fully mounted
    const timeoutId = setTimeout(() => {
      checkFirebase();
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [initializationAttempted]); // Only depend on initializationAttempted to prevent StrictMode issues
  
  // Create a stable context value that won't change on every render
  const contextValue = React.useMemo(() => ({
    firebaseInitialized,
    initializationError
  }), [firebaseInitialized, initializationError]);
  
  return (
    <FirebaseContext.Provider value={contextValue}>
      {children}
    </FirebaseContext.Provider>
  );
}

export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (context === null) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};
